import React, { useState } from 'react';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';

export interface ChartHopCredentialsFormProps {
  onSubmit: (apiKey: string) => void;
  onTest: (apiKey: string) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
  error: string | null;
}

export const ChartHopCredentialsForm = ({
  onSubmit,
  onTest,
  isLoading,
  error,
}: ChartHopCredentialsFormProps) => {
  const [apiKey, setApiKey] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const trimmedKey = apiKey.trim();
  const isValid = trimmedKey.length > 0;

  const handleSubmit = () => {
    if (!isValid) {
      return;
    }
    onSubmit(trimmedKey);
  };

  const handleTest = async () => {
    if (!isValid) {
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    const result = await onTest(trimmedKey);

    setIsTesting(false);
    setTestResult({
      success: result.success,
      message: result.success
        ? 'Connection successful! API key is valid.'
        : result.error || 'Connection failed',
    });
  };

  return (
    <Spacing size="md" direction="vertical">
      <S2Typography
        variant="body-6"
        translatedContent="Enter your ChartHop API token. Sigma authenticates to https://api.charthop.com with an Authorization Bearer header."
      />

      <TextInput
        type="password"
        label="API Key"
        placeholder="Enter your ChartHop API token"
        value={apiKey}
        onChange={(e) => setApiKey(e.currentTarget.value)}
        required
        disabled={isLoading || isTesting}
        data-qa="chart-hop-api-key-input"
      />

      {testResult && (
        <S2Typography
          variant="body-6"
          textColorVariant={testResult.success ? 'green-800' : 'red-500'}
          translatedContent={testResult.message}
        />
      )}

      {error && (
        <S2Typography
          variant="body-6"
          textColorVariant="red-500"
          translatedContent={error}
        />
      )}

      <Spacing size="sm" direction="horizontal">
        <Button
          variant="outline"
          size="medium"
          data-qa="chart-hop-test-button"
          behavior={{
            type: 'onClick',
            onClick: () => {
              void handleTest();
            },
          }}
          disabled={!isValid || isTesting || isLoading}
        >
          {isTesting ? 'Validating...' : 'Validate Connection'}
        </Button>

        <Button
          variant="primary"
          size="medium"
          data-qa="chart-hop-submit-button"
          behavior={{
            type: 'onClick',
            onClick: () => {
              handleSubmit();
            },
          }}
          disabled={!isValid || isLoading || isTesting}
        >
          Connect ChartHop
        </Button>
      </Spacing>
    </Spacing>
  );
};
